import { useCallback, useEffect, useState } from 'react';
import {
  opsApi,
  type OpsDepartmentDto, type OpsStaffSummaryDto, type OpsTaskDto, type OpsTaskInput,
  type OpsTaskPriority, type OpsTaskStatus,
} from '../api';
import { OpsPanelState, OpsTable, RowActions, opsErrorText, useOpsPanel } from './ops/panel';

/**
 * TICK-122 — the workboard: tasks, their owners, and what blocks what.
 *
 * The dependency editor is the part worth the extra screen. The server checks the whole graph and
 * refuses an edge that would close a cycle, naming the path it found; showing that sentence is the
 * difference between "you cannot do that" and "A → B → C → A is why". A cycle in a plan is a plan
 * nobody can start, and the operator is the only one who can decide which link to cut.
 *
 * Owners are shown twice on purpose. `ownerName` is a real crew record; `importedOwnerName` is the
 * free text an import carried in. Collapsing them into one column would hide which tasks still need
 * a person attached, and would invite retyping a name as free text — the thing the reference exists
 * to stop (TICK-111 D1).
 */

const STATUS_LABEL: Record<OpsTaskStatus, string> = {
  todo: 'ยังไม่เริ่ม',
  in_progress: 'กำลังทำ',
  pending_approval: 'รออนุมัติ',
  done: 'เสร็จแล้ว',
};

const PRIORITY_LABEL: Record<OpsTaskPriority, string> = {
  low: 'ต่ำ',
  medium: 'ปานกลาง',
  high: 'สูง',
  urgent: 'ด่วน',
};

const STATUS_ORDER: OpsTaskStatus[] = ['todo', 'in_progress', 'pending_approval', 'done'];
const PRIORITY_ORDER: OpsTaskPriority[] = ['low', 'medium', 'high', 'urgent'];

export function OpsTasksPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [tasks, setTasks] = useState<OpsTaskDto[] | null>(null);
  const [departments, setDepartments] = useState<OpsDepartmentDto[]>([]);
  const [crew, setCrew] = useState<OpsStaffSummaryDto[]>([]);
  const [loadError, setLoadError] = useState('');
  const [editing, setEditing] = useState<OpsTaskDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [linking, setLinking] = useState<OpsTaskDto | null>(null);

  const load = useCallback(async () => {
    try {
      const [t, d, s] = await Promise.all([
        opsApi.listTasks(token, eventSlug),
        opsApi.listDepartments(token, eventSlug),
        opsApi.listStaff(token, eventSlug),
      ]);
      setTasks(t);
      setDepartments(d);
      setCrew(s);
    } catch (e) {
      setLoadError(opsErrorText(e, 'โหลดแผนงานไม่สำเร็จ'));
    }
  }, [token, eventSlug]);

  useEffect(() => { void load(); }, [load]);

  /*
   * `describeIds` is the one thing the shared hook cannot know: the cycle arrives as a list of task
   * ids, and only this screen holds the titles. Everything else about the failure — the sentence,
   * the reload, the version conflict — is the same everywhere and lives in `useOpsPanel`.
   */
  const { busy, error, run } = useOpsPanel(load, {
    describeIds: (ids) => ids.map((id) => tasks?.find((t) => t.id === id)?.title ?? id),
  });

  if (!tasks) return <OpsPanelState error={loadError} />;

  const title = (id: string) => tasks.find((t) => t.id === id)?.title ?? id;

  return (
    <div className="ops-panel">
      {error && <div className="form-error">{error}</div>}

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--dim)', lineHeight: 1.7 }}>
          งานของอีเวนต์นี้ · “ขึ้นกับ” คืองานที่ต้องเสร็จก่อน — ระบบปฏิเสธวงจรที่ทำให้ไม่มีงานไหนเริ่มได้
        </p>
        <button className="btn-primary" disabled={busy} onClick={() => setCreating(true)}>เพิ่มงาน</button>
      </div>

      {tasks.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>ยังไม่มีงานในแผนของอีเวนต์นี้</div>
      ) : (
        <OpsTable head={<><th>งาน</th><th>ฝ่าย</th><th>ผู้รับผิดชอบ</th><th>ความสำคัญ</th><th>กำหนดส่ง</th><th>สถานะ</th><th>ขึ้นกับ</th><th /></>}>
              {tasks.map((t) => (
                <tr key={t.id}>
                  <td>{t.title}</td>
                  <td style={{ color: 'var(--dim)' }}>{t.departmentName ?? '—'}</td>
                  <td>
                    {t.ownerName ?? (t.importedOwnerName
                      ? <span style={{ color: 'var(--dim)' }} title="ชื่อจากการนำเข้า ยังไม่ได้ผูกกับคนในทีมงาน">{t.importedOwnerName} · ยังไม่ผูก</span>
                      : <span style={{ color: 'var(--dim)' }}>—</span>)}
                  </td>
                  <td>{PRIORITY_LABEL[t.priority]}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{t.slaDate}</td>
                  <td>
                    <select
                      aria-label={`สถานะของ ${t.title}`}
                      value={t.status}
                      disabled={busy}
                      onChange={(e) => void run(() => opsApi.updateTask(
                        token, t.id, { status: e.target.value as OpsTaskStatus, version: t.version }, eventSlug,
                      ))}
                    >
                      {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                  </td>
                  <td style={{ fontSize: 11.5, color: 'var(--dim)' }}>
                    {t.dependsOn.length === 0 ? '—' : t.dependsOn.map(title).join(' · ')}
                  </td>
                  <td>
                    <RowActions>
                      <button className="btn-link" disabled={busy} onClick={() => setEditing(t)}>แก้ไข</button>
                      <button className="btn-link" disabled={busy} onClick={() => setLinking(t)}>ขึ้นกับ</button>
                      <button
                        className="btn-link"
                        disabled={busy}
                        onClick={() => {
                          if (!confirm(`ลบงาน "${t.title}"?`)) return;
                          void run(() => opsApi.deleteTask(token, t.id, t.version, eventSlug));
                        }}
                      >
                        ลบ
                      </button>
                    </RowActions>
                  </td>
                </tr>
              ))}
        </OpsTable>
      )}

      {(creating || editing) && (
        <TaskEditor
          task={editing}
          departments={departments}
          crew={crew}
          busy={busy}
          onCancel={() => { setCreating(false); setEditing(null); }}
          onSave={async (input) => {
            const ok = await run(() => (editing
              ? opsApi.updateTask(token, editing.id, { ...input, version: editing.version }, eventSlug)
              : opsApi.createTask(token, input, eventSlug)));
            if (ok) { setCreating(false); setEditing(null); }
          }}
        />
      )}

      {linking && (
        <DependencyEditor
          task={linking}
          tasks={tasks}
          busy={busy}
          onCancel={() => setLinking(null)}
          onSave={async (dependsOn) => {
            const ok = await run(() => opsApi.setTaskDependencies(token, linking.id, dependsOn, eventSlug));
            if (ok) setLinking(null);
          }}
        />
      )}
    </div>
  );
}

function TaskEditor({ task, departments, crew, busy, onCancel, onSave }: {
  task: OpsTaskDto | null;
  departments: OpsDepartmentDto[];
  crew: OpsStaffSummaryDto[];
  busy: boolean;
  onCancel: () => void;
  onSave: (input: OpsTaskInput) => void;
}) {
  const [title, setTitle] = useState(task?.title ?? '');
  const [departmentId, setDepartmentId] = useState(task?.departmentId ?? '');
  const [ownerStaffId, setOwnerStaffId] = useState(task?.ownerStaffId ?? '');
  const [priority, setPriority] = useState<OpsTaskPriority>(task?.priority ?? 'medium');
  const [status, setStatus] = useState<OpsTaskStatus>(task?.status ?? 'todo');
  const [slaDate, setSlaDate] = useState(task?.slaDate ?? '');
  const [startDate, setStartDate] = useState(task?.startDate ?? '');
  const [description, setDescription] = useState(task?.description ?? '');

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px' }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 13.5 }}>{task ? `แก้ไข ${task.title}` : 'เพิ่มงานใหม่'}</h4>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5 }}>
        ชื่องาน
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>

      <div className="ticket-edit-grid" style={{ marginTop: 12 }}>
        <label>
          ฝ่าย
          <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="">— ไม่ระบุ —</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </label>
        <label>
          ผู้รับผิดชอบ
          <select value={ownerStaffId} onChange={(e) => setOwnerStaffId(e.target.value)}>
            <option value="">— ไม่ระบุ —</option>
            {crew.map((m) => <option key={m.id} value={m.id}>{m.displayName}</option>)}
          </select>
        </label>
        <label>
          ความสำคัญ
          <select value={priority} onChange={(e) => setPriority(e.target.value as OpsTaskPriority)}>
            {PRIORITY_ORDER.map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
          </select>
        </label>
        <label>
          สถานะ
          <select value={status} onChange={(e) => setStatus(e.target.value as OpsTaskStatus)}>
            {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </label>
        <label>
          วันเริ่มงาน
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label>
          กำหนดส่ง
          <input type="date" value={slaDate} onChange={(e) => setSlaDate(e.target.value)} />
        </label>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, marginTop: 12 }}>
        รายละเอียด
        <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      <div className="admin-actions" style={{ marginTop: 12 }}>
        <button
          className="btn-primary"
          disabled={busy || !title.trim() || !slaDate}
          onClick={() => onSave({
            title: title.trim(),
            departmentId: departmentId || null,
            ownerStaffId: ownerStaffId || null,
            priority,
            status,
            slaDate,
            startDate: startDate || null,
            description: description.trim(),
          })}
        >
          บันทึก
        </button>
        <button className="btn-link" onClick={onCancel}>ยกเลิก</button>
      </div>
    </div>
  );
}

function DependencyEditor({ task, tasks, busy, onCancel, onSave }: {
  task: OpsTaskDto;
  tasks: OpsTaskDto[];
  busy: boolean;
  onCancel: () => void;
  onSave: (dependsOn: string[]) => void;
}) {
  const [selected, setSelected] = useState<string[]>(task.dependsOn);
  // A task depending on itself is refused by the server; not offering it is kinder than explaining it.
  const candidates = tasks.filter((t) => t.id !== task.id);

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px' }}>
      <h4 style={{ margin: '0 0 4px', fontSize: 13.5 }}>“{task.title}” ขึ้นกับงานไหน</h4>
      <p style={{ margin: '0 0 12px', fontSize: 11.5, color: 'var(--dim)', lineHeight: 1.7 }}>
        เลือกงานที่ต้องเสร็จก่อนงานนี้ · ถ้าเลือกแล้วเกิดวงจร ระบบจะบอกว่าเส้นทางไหนวนกลับมา
      </p>

      {candidates.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>ยังไม่มีงานอื่นให้เลือก</div>}

      {candidates.map((t) => (
        <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={selected.includes(t.id)}
            onChange={(e) => setSelected(e.target.checked
              ? [...selected, t.id]
              : selected.filter((id) => id !== t.id))}
          />
          {t.title}
        </label>
      ))}

      <div className="admin-actions" style={{ marginTop: 12 }}>
        <button className="btn-primary" disabled={busy} onClick={() => onSave(selected)}>บันทึก</button>
        <button className="btn-link" onClick={onCancel}>ยกเลิก</button>
      </div>
    </div>
  );
}
